// When to ask the user how a conversation is going. Message-based, no cooldown clocks: the card appears
// once per thread after a few settled turns, in every thread. The only thing that silences it is the user
// dismissing three times in a row, and that silence lasts only until the end of the day.
//
// The per-message thumbs are unthrottled and carry the volume; this popup is the coverage mechanism for
// users who never hover, so it is deliberately at the aggressive end — `outcome='dismissed'` rows are what
// make the trade measurable rather than a matter of taste.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, FeedbackRating, FeedbackRequest, FeedbackTag, Mode } from '../types/agent';

/** Settled assistant turns before the ribbon is offered. Deep enough that there is something to judge,
 *  shallow enough to catch the conversation while it is still fresh. Raise this rather than adding a
 *  cooldown if prompts start landing mid-task (`forcedByDev` is how to look at it without waiting). */
export const PROMPT_AFTER_TURNS = 4;
/** The turn must have been settled this long, and the composer empty: a card that appears mid-sentence is
 *  the whole of the fatigue problem. With no cooldown to fall back on, this guard is load-bearing. */
export const IDLE_BEFORE_MS = 15_000;
export const STOP_AFTER_DISMISSALS = 3;

const SEEN_KEY = 'agent_v2_feedback_seen';
const THREADS_KEY = 'agent_v2_feedback_threads';
/** Prompted thread ids kept, LRU — enough to survive a pane reload mid-thread without growing forever. */
const THREADS_KEPT = 20;

export interface SeenState {
  /** Consecutive dismissals; any answer resets it to 0. */
  streak: number;
  /** Calendar day the streak belongs to (YYYY-MM-DD, local). A different day means start over. */
  day: string;
}

export const EMPTY_SEEN: SeenState = { streak: 0, day: '' };

export function todayKey(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Storage can be unavailable in an Office frame (the reason `tokenStore` layers its stores), so every
 *  access is guarded and the counter falls back to memory for the pane session. The cap is best-effort. */
let memorySeen: SeenState = { ...EMPTY_SEEN };
let memoryThreads: string[] = [];

export function readSeen(today: string): SeenState {
  let raw: SeenState = memorySeen;
  try {
    const stored = localStorage.getItem(SEEN_KEY);
    if (stored) raw = JSON.parse(stored) as SeenState;
  } catch {
    /* private mode / quota / blocked storage — the in-memory value stands */
  }
  if (!raw || typeof raw.streak !== 'number') return { ...EMPTY_SEEN, day: today };
  // A streak from an earlier day has expired: the silence a dismisser earned lasts one day, not forever.
  return raw.day === today ? raw : { streak: 0, day: today };
}

export function writeSeen(next: SeenState): void {
  memorySeen = next;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(next));
  } catch {
    /* keep it in memory only */
  }
}

export function readThreads(): string[] {
  try {
    const stored = localStorage.getItem(THREADS_KEY);
    if (stored) return JSON.parse(stored) as string[];
  } catch {
    /* fall through to memory */
  }
  return memoryThreads;
}

export function rememberThread(threadId: string): void {
  const next = [threadId, ...readThreads().filter((t) => t !== threadId)].slice(0, THREADS_KEPT);
  memoryThreads = next;
  try {
    localStorage.setItem(THREADS_KEY, JSON.stringify(next));
  } catch {
    /* keep it in memory only */
  }
}

/** Settled assistant turns with something to show.
 *
 *  Prefers distinct `turnIndex` values: live streaming produces one bubble per turn, but a reloaded thread
 *  produces one per AIMessage, so counting bubbles would over-count a restored conversation and could trip
 *  the prompt on arrival. Falls back to the bubble count against a server that sends no `turn` event. */
export function assistantTurnCount(messages: ChatMessage[]): number {
  const settled = messages.filter(
    (m) => m.role === 'assistant' && !m.pending && !m.error && (!!m.content.trim() || !!m.toolCards?.length),
  );
  if (settled.length && settled.every((m) => m.turnIndex != null)) {
    return new Set(settled.map((m) => m.turnIndex)).size;
  }
  return settled.length;
}

/** Dev escape hatch: show the card on the next settled turn, ignoring every cadence gate — otherwise
 *  evaluating a design change means playing four turns and waiting out the idle timer each time.
 *
 *  Turn it on from the pane's devtools console with
 *    `localStorage.setItem('agent_v2_feedback_force', '1')`
 *  or by adding `?feedback=1` to the taskpane URL; `removeItem` turns it off. Opt-in only, so it can
 *  never fire for a real user. A thread is still required — the card has to have something to rate. */
export const FORCE_KEY = 'agent_v2_feedback_force';

export function forcedByDev(): boolean {
  try {
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('feedback') === '1') return true;
    return localStorage.getItem(FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export interface ShouldPromptArgs {
  threadId: string | null;
  assistantTurns: number;
  busy: boolean;
  /** The composer holds unsent text: the user is mid-thought, do not interrupt. */
  composerDirty: boolean;
  /** When the last turn settled (ms epoch); null while nothing has settled yet. */
  settledAt: number | null;
  prompted: string[];
  seen: SeenState;
  now: number;
  /** Dev override (`forcedByDev`): skip the cadence gates so the card can be looked at on demand. */
  forced?: boolean;
}

export function shouldPrompt(a: ShouldPromptArgs): boolean {
  // A thread to rate and a quiet moment are the two things even the override cannot skip.
  if (!a.threadId || a.busy) return false;
  // The override still honours once-per-thread: otherwise the card reappears two seconds after every
  // dismissal (unclosable, and a `dismissed` row each time). Start a new chat to see it again.
  if (a.forced) return !a.prompted.includes(a.threadId);
  if (a.composerDirty) return false;
  if (a.assistantTurns < PROMPT_AFTER_TURNS) return false;
  if (a.seen.streak >= STOP_AFTER_DISMISSALS) return false;
  if (a.prompted.includes(a.threadId)) return false;
  if (a.settledAt == null || a.now - a.settledAt < IDLE_BEFORE_MS) return false;
  return true;
}

export interface FeedbackPromptDeps {
  threadId: string | null;
  messages: ChatMessage[];
  busy: boolean;
  composerDirty: boolean;
  mode: Mode;
  workbookId: string | null;
  /** Injected rather than imported so the hook stays free of `apiService` (UI layer does no fetch). */
  send: (body: FeedbackRequest) => Promise<unknown>;
}

export interface FeedbackPromptApi {
  visible: boolean;
  /** Records the rating. Leaves the card up so it can show its "thanks" beat; call `close` after. */
  submit: (rating: FeedbackRating, tags: FeedbackTag[], comment: string) => Promise<void>;
  /** Explicit dismissal (× / "Not now"), or the prompt being superseded by a thread change. */
  skip: () => void;
  /** Hide without recording anything — the card is done talking (after "thanks", or a failed send). */
  close: () => void;
}

export function useFeedbackPrompt(deps: FeedbackPromptDeps): FeedbackPromptApi {
  const { threadId, messages, busy, composerDirty, mode, workbookId, send } = deps;
  const [visible, setVisible] = useState(false);
  /** When the last turn settled, tagged with the thread it belongs to (see the effect below). */
  const settled = useRef<{ threadId: string | null; at: number | null }>({ threadId: null, at: null });
  /** What the visible card is asking about. Snapshotted when it appears, because a dismissal recorded on
   *  thread change must name the thread the user was actually looking at, not the one they moved to. */
  const shown = useRef<{ threadId: string; turns: number } | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const metaRef = useRef({ mode, workbookId });
  metaRef.current = { mode, workbookId };
  const turns = useMemo(() => assistantTurnCount(messages), [messages]);

  // A prompt shown but never answered still counts as a dismissal — otherwise the response rate only ever
  // sees the users who clicked ×, and looks far better than it is.
  const dismiss = useCallback((target: { threadId: string; turns: number }) => {
    const today = todayKey();
    writeSeen({ streak: readSeen(today).streak + 1, day: today });
    rememberThread(target.threadId);
    void sendRef.current({
      thread_id: target.threadId, outcome: 'dismissed', source: 'popup', turns_seen: target.turns,
      mode: metaRef.current.mode, workbook_id: metaRef.current.workbookId,
    }).catch(() => undefined);
  }, []);

  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;

  // Remember when the last turn settled, so the card waits for a lull rather than landing on the user.
  //
  // `threadId` is a dependency and the stamp carries the thread it belongs to, because this effect owns
  // the timer alone. An earlier version also cleared it from the [threadId] effect below, which runs
  // second: on a restored thread (what happens on every pane open) the stamp was set and then wiped, and
  // since neither `busy` nor `turns` changes afterwards it was never re-armed — the card could not appear
  // until the user sent another message.
  useEffect(() => {
    if (settled.current.threadId !== threadId) settled.current = { threadId, at: null };
    if (busy) settled.current.at = null;
    else if (settled.current.at == null && turns > 0) settled.current.at = Date.now();
  }, [busy, turns, threadId]);

  // Leaving a shown prompt unanswered (new chat, another thread, pane closed) is a dismissal.
  useEffect(() => {
    setVisible(false);
    return () => {
      const target = shown.current;
      shown.current = null;
      if (target) dismissRef.current(target);
    };
  }, [threadId]);

  useEffect(() => {
    if (visible) return;
    const evaluate = () => {
      if (!threadId) return;
      if (shouldPrompt({ threadId, assistantTurns: turns, busy, composerDirty, settledAt: settled.current.at,
                         prompted: readThreads(), seen: readSeen(todayKey()), now: Date.now(),
                         forced: forcedByDev() })) {
        shown.current = { threadId, turns };
        setVisible(true);
      }
    };
    evaluate();
    // `settledAt` matures on its own, so re-check while the lull elapses instead of awaiting a re-render.
    const timer = window.setInterval(evaluate, 2_000);
    return () => window.clearInterval(timer);
  }, [threadId, turns, busy, composerDirty, visible]);

  const submit = useCallback(
    async (rating: FeedbackRating, tags: FeedbackTag[], comment: string) => {
      const target = shown.current ?? (threadId ? { threadId, turns } : null);
      if (!target) return;
      shown.current = null; // answered: the unmount hook must not also record a dismissal
      writeSeen({ streak: 0, day: todayKey() }); // engaging buys back the full budget
      rememberThread(target.threadId);
      await send({ thread_id: target.threadId, outcome: 'rated', rating, tags,
                   comment: comment.trim() || null, source: 'popup', turns_seen: target.turns,
                   mode, workbook_id: workbookId });
    },
    [threadId, turns, mode, workbookId, send],
  );

  const skip = useCallback(() => {
    const target = shown.current;
    shown.current = null;
    setVisible(false);
    if (target) dismiss(target);
  }, [dismiss]);

  const close = useCallback(() => {
    shown.current = null;
    setVisible(false);
  }, []);

  return { visible, submit, skip, close };
}
